import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { apiClient } from "../../lib/api";
import { Btn } from "../../lib/primitives";
import { Icons, Cascade } from "../../lib/icons";
import { OnboardingShell } from "./OnboardingShell";

/**
 * Step 4: completion screen.
 *
 * "Go to dashboard" PATCHes `completedAt = now`, then routes to /quickstart
 * (this is the operator's FIRST visit — the wizard literally just finished,
 * so we know they're brand new). The Quickstart screen picks up the
 * integration thread; once they navigate away from it, return visits to
 * the wizard's Done link are nearly nonexistent so the "first vs nth visit"
 * distinction doesn't pay rent here. If the operator wants to land on the
 * Dashboard instead, the Quickstart's CTA links jump there in one click.
 *
 * The completedAt PATCH still flips the wizard guard off, so subsequent
 * page loads bypass /onboarding/* entirely.
 */
export function Done() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const finish = useMutation({
    mutationFn: () =>
      apiClient.onboarding.patch({
        step: "done",
        completedAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding"] });
      qc.invalidateQueries({ queryKey: ["quickstart"] });
      navigate("/quickstart", { replace: true });
    },
  });

  return (
    <OnboardingShell current="done">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "40px 0",
        }}
      >
        <Cascade size={64} />
        <div className="ad-eyebrow" style={{ marginTop: 24, marginBottom: 6 }}>
          Done
        </div>
        <h1 className="ad-h1" style={{ fontSize: 32, marginBottom: 12 }}>
          You're <em>all set.</em>
        </h1>
        <div
          className="ad-sub"
          style={{ maxWidth: 460, marginBottom: 28 }}
        >
          Add the API key to your server, send people to our sign-in page, and
          you're live. The Get started checklist walks you through the rest.
        </div>
        <Btn
          kind="primary"
          onClick={() => finish.mutate()}
          disabled={finish.isPending}
          trailing={<Icons.ArrowR size={14} />}
        >
          {finish.isPending ? "Finishing…" : "Go to Quickstart"}
        </Btn>
      </div>
    </OnboardingShell>
  );
}
